import React, {useState} from 'react'

import {Alert, Badge, Button, Card, Colors, H3, H5, IconButton, P, S2, Tab, Tabs, Row} from '../../components/UI/index.js'
import {
    formSpacing,
    AddressSearch,
    Check,
    ColorPicker,
    CurrencyInput,
    DatePicker,
    Editor,
    FileUpload,
    FloatInput,
    FormRow,
    FormStack,
    Input,
    MetricInput,
    Option,
    RadioButton,
    RadioGroup,
    Select,
    Slider,
    Steps,
    TextArea,
    TimePicker,
    Toggle,
    UploadImage,
    VerificationCodeInput
} from '../../components/UI/forms/index.js'
import IconPicker from '../../components/UI/forms/IconPicker.js'
import PostalCodeAutocomplete from '../../components/UI/forms/PostalCodeAutocomplete.js'
import NewAddress from '../../components/addresses/NewAddress.js'

// Style guide of the form components: every component in every state, so a change to the form tokens
// (forms/styles.ts, forms/choiceStyles.ts, forms/FieldBox.tsx) can be checked on one page. Reached at /componenten.

const options = [
    {value: 'bike', title: 'Fiets'},
    {value: 'cargo', title: 'Bakfiets'},
    {value: 'van', title: 'Bus'},
    {value: 'truck', title: 'Vrachtwagen'}
]

// A title that doesn't fit, and an option with info (shown next to the caret), for the invalid icon
const longOptions = [
    ...options,
    {value: 'long', title: 'Elektrische bakfiets met huif en extra lange laadbak'},
    {value: 'info', title: 'Bakfiets met koelbox voor gekoelde zendingen', info: 'Option info, shown next to the caret'}
]

const longValue = 'Damrak 1, 1012 LG Amsterdam, Noord-Holland, Nederland'

const iconOptions = [
    {value: 'bike', title: 'Fiets', icon: 'mdi mdi-bike', showIconInValue: true},
    {value: 'van', title: 'Bus', icon: 'mdi mdi-van-utility', showIconInValue: true},
    {value: 'truck', title: 'Vrachtwagen', icon: 'mdi mdi-truck', iconColor: Colors.errorBright, showIconInValue: true}
]

const icons = ['bike', 'bike-fast', 'bicycle-cargo', 'truck-trailer', 'car-side', 'van-utility', 'truck', 'moped', 'walk']

const radioOptions = [
    {value: 'day', label: 'Dag'},
    {value: 'week', label: 'Week'},
    {value: 'month', label: 'Maand'}
]

const sampleAddress = {...NewAddress(), street: 'Damrak', nr: '1', postalCode: '1012 LG', city: 'Amsterdam'}

// The standard states every field is shown in. Fields without a placeholder or invalid state leave those out.
const STATES = [
    {key: 'default', label: 'Default', empty: true},
    {key: 'value', label: 'With value'},
    {key: 'placeholder', label: 'Placeholder', empty: true, placeholder: true},
    {key: 'disabled', label: 'Disabled', disabled: true},
    {key: 'readOnly', label: 'ReadOnly', readOnly: true},
    {key: 'invalid', label: 'Invalid', isInvalid: true},
    {key: 'invalidMessage', label: 'Invalid with message', isInvalid: 'This value is not valid'},
    {key: 'info', label: 'With info', info: 'Explanation shown in the info tooltip'}
]

const PER_ROW = 4

const chunk = (items, size) => {
    const rows = []
    for (let i = 0; i < items.length; i += size) {
        rows.push(items.slice(i, i + size))
    }
    return rows
}

// Gives each demo its own local state, so every field on the page is interactive
const Stateful = ({initial, children}) => {
    const [value, setValue] = useState(initial)
    return children(value, setValue)
}

/**
 * Renders a field in all STATES, in FormRows of PER_ROW.
 * render(props, value, setValue) gets the state props (label, disabled, readOnly, isInvalid, info, placeholder).
 */
const FieldStates = ({value, emptyValue = '', placeholder, noInvalid, render}) => {
    const states = STATES.filter((state) => {
        if (state.placeholder && placeholder === undefined) return false
        if (state.isInvalid && noInvalid) return false
        return true
    })

    return chunk(states, PER_ROW).map((row, index) => (
        <FormRow key={index}>
            {row.map((state) => (
                <Stateful key={state.key} initial={state.empty ? emptyValue : value}>
                    {(stateValue, setValue) => render({
                        label: state.label,
                        disabled: state.disabled,
                        readOnly: state.readOnly,
                        isInvalid: state.isInvalid,
                        info: state.info,
                        placeholder: state.placeholder ? placeholder : undefined
                    }, stateValue, setValue)}
                </Stateful>
            ))}
            {/* Keeps the fields of a shorter last row as wide as the ones above */}
            {Array.from({length: PER_ROW - row.length}).map((_, i) => <div key={`filler${i}`} style={{flex: 1}} />)}
        </FormRow>
    ))
}

const Section = ({title, children}) => (
    <FormStack gap={formSpacing.section}>
        <H3 style={{margin: 0}}>{title}</H3>
        {children}
    </FormStack>
)

const Demo = ({name, note, children}) => (
    <FormStack>
        <H5 style={{margin: 0}}>{name}</H5>
        {note && <S2 style={{margin: 0}}>{note}</S2>}
        {children}
    </FormStack>
)

// vertical: a list instead of a row, for controls that spread their label and control over the full width (Toggle)
const ChoiceStates = ({vertical, render}) => {
    const items = [
        {key: 'off', label: 'Off', checked: false},
        {key: 'on', label: 'On', checked: true},
        {key: 'disabledOff', label: 'Disabled off', checked: false, disabled: true},
        {key: 'disabledOn', label: 'Disabled on', checked: true, disabled: true},
        {key: 'readOnly', label: 'ReadOnly on', checked: true, readOnly: true},
        {key: 'info', label: 'With info', checked: false, info: 'Explanation shown in the info tooltip'}
    ].map((state) => (
        <Stateful key={state.key} initial={state.checked}>
            {(checked, setChecked) => render({label: state.label, disabled: state.disabled, readOnly: state.readOnly, info: state.info}, checked, setChecked)}
        </Stateful>
    ))

    return vertical ? <FormStack style={{maxWidth: 300}}>{items}</FormStack> : <FormRow>{items}</FormRow>
}

const valuesToLabel = (value1, value2) => {
    const time = (value) => `${String(Math.floor(value / 4)).padStart(2, '0')}:${String((value % 4) * 15).padStart(2, '0')}`
    return {label1: time(value1), label2: time(value2)}
}

const TextFields = () => (
    <Section title='Text fields'>
        <Demo name='Input'>
            <FieldStates
                value='Damrak 1'
                placeholder='Type something'
                render={(props, value, setValue) => <Input {...props} value={value} onChange={(event) => setValue(event.target.value)} />}
            />

            <FormRow>
                <Stateful initial='12'>{(value, setValue) => <Input label='Append text' append='km' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial='8'>{(value, setValue) => <Input label='Append long text' append='per 100km' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial='Amsterdam'>{(value, setValue) => <Input label='Prepend icon' prepend='mdi mdi-magnify' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial=''>{(value, setValue) => <Input label='Prepend icon, empty' prepend='mdi mdi-magnify' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
            </FormRow>

            <FormRow>
                <Stateful initial='3'>{(value, setValue) => <Input label='Append icon' append='mdi mdi-weight-kilogram' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial='12'>{(value, setValue) => <Input label='Append text, invalid' append='km' isInvalid value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial='geheim'>{(value, setValue) => <Input label='Type password' type='password' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial=''>
                    {(value, setValue) => (
                        <Input
                            label='Suggestions (type a city)'
                            suggestions={['Amsterdam', 'Rotterdam', 'Utrecht', 'Den Haag', 'Eindhoven']}
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                        />
                    )}
                </Stateful>
            </FormRow>

            <FormRow>
                <Stateful initial={longValue}>{(value, setValue) => <Input label='Long value, invalid' isInvalid value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial={longValue}>{(value, setValue) => <Input label='Append long text, invalid' append='per 100km' isInvalid value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial={longValue}>{(value, setValue) => <Input label='Append icon, invalid' append='mdi mdi-weight-kilogram' isInvalid value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial={longValue}>{(value, setValue) => <Input label='Password, invalid' type='password' isInvalid value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
            </FormRow>
        </Demo>

        <Demo name='TextArea' note='Grows with its content up to field.textAreaMaxRows lines, then it scrolls. rows is the minimum.'>
            <FieldStates
                value={'First line\nSecond line'}
                render={(props, value, setValue) => <TextArea {...props} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
            <FormRow>
                <Stateful initial={`${longValue}. ${longValue}.`}>{(value, setValue) => <TextArea label='Long line, invalid' isInvalid value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial={Array.from({length: 14}, (_, index) => `Line ${index + 1}`).join('\n')}>{(value, setValue) => <TextArea label='Many lines (scrolls)' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial='One line'>{(value, setValue) => <TextArea label='rows={3}' rows={3} value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
            </FormRow>
        </Demo>

        <Demo name='Editor' note='Type { to insert a tag. onChange is only called on blur.'>
            <FieldStates
                value='Beste {klant}, welkom bij {bedrijfsnaam}.'
                render={(props, value, setValue) => <Editor {...props} tags={['bedrijfsnaam', 'klant']} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
            <FormRow>
                <Stateful initial='Order {ordernummer}'>
                    {(value, setValue) => <Editor label='As input' as='input' tags={['ordernummer']} value={value} onChange={(event) => setValue(event.target.value)} />}
                </Stateful>
                <Stateful initial={`Order {ordernummer} van {klant} is onderweg naar ${longValue}`}>
                    {(value, setValue) => <Editor label='As input, long and invalid' as='input' isInvalid tags={['ordernummer', 'klant']} value={value} onChange={(event) => setValue(event.target.value)} />}
                </Stateful>
            </FormRow>
        </Demo>
    </Section>
)

const NumberFields = () => (
    <Section title='Number fields'>
        <Demo name='CurrencyInput' note='Prepend is € by default. onChange on blur/Enter.'>
            <FieldStates
                value='12,50'
                render={(props, value, setValue) => <CurrencyInput {...props} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
            <FormRow>
                <Stateful initial='0,25'>{(value, setValue) => <CurrencyInput label='Append text' append='per km' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <div style={{flex: 1}} />
            </FormRow>
        </Demo>

        <Demo name='MetricInput' note='Stored in grams, shown in kg (divisionFactor 1000).'>
            <FieldStates
                value={2500}
                emptyValue={undefined}
                placeholder={1000}
                render={(props, value, setValue) => <MetricInput {...props} divisionFactor={1000} append='kg' value={value} onChange={(event) => setValue(event.target.value)} />}
            />
            <FormRow>
                <Stateful initial={12000}>{(value, setValue) => <MetricInput label='Append long text' divisionFactor={1000} append='per 100km' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial={300}>{(value, setValue) => <MetricInput label='Prepend icon' divisionFactor={100} prepend='mdi mdi-ruler' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
            </FormRow>
        </Demo>

        <Demo name='FloatInput'>
            <FieldStates
                value='1.5'
                render={(props, value, setValue) => <FloatInput {...props} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
            <FormRow>
                <Stateful initial='21'>{(value, setValue) => <FloatInput label='Append text' append='%' value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <div style={{flex: 1}} />
            </FormRow>
        </Demo>

        <Demo name='Steps'>
            <FieldStates
                value={3}
                emptyValue={0}
                render={(props, value, setValue) => <Steps {...props} min={0} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
        </Demo>
    </Section>
)

const SelectFields = () => (
    <Section title='Select'>
        <Demo name='Select (single)'>
            <FieldStates
                value='cargo'
                placeholder='Choose a vehicle'
                render={(props, value, setValue) => <Select {...props} options={options} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
        </Demo>

        <Demo name='Select (multi)' note='Selected values show as pills.'>
            <FieldStates
                value={['bike', 'van']}
                emptyValue={[]}
                placeholder='Choose vehicles'
                render={(props, values, setValues) => <Select {...props} options={options} values={values} onChange={(event) => setValues(event.target.values)} />}
            />
            <FormRow>
                <Stateful initial={['bike', 'cargo', 'van', 'truck']}>
                    {(values, setValues) => <Select label='Many values, selectAll and copy' selectAll copy options={options} values={values} onChange={(event) => setValues(event.target.values)} />}
                </Stateful>
                <div style={{flex: 1}} />
            </FormRow>
        </Demo>

        <Demo name='Select (long values)'>
            <FormRow>
                <Stateful initial='long'>{(value, setValue) => <Select label='Long value, invalid' isInvalid options={longOptions} value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial='info'>{(value, setValue) => <Select label='Option info, invalid' isInvalid options={longOptions} value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial={['bike', 'cargo', 'van', 'truck']}>
                    {(values, setValues) => <Select label='Copy, invalid' copy isInvalid options={options} values={values} onChange={(event) => setValues(event.target.values)} />}
                </Stateful>
            </FormRow>
        </Demo>

        <Demo name='Select (icons)'>
            <FormRow>
                <Stateful initial='van'>{(value, setValue) => <Select label='With icons' options={iconOptions} value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial='truck'>{(value, setValue) => <Select label='Icon colour, disabled' disabled options={iconOptions} value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <Stateful initial={[]}>{(values, setValues) => <Select label='Multi with icons' options={iconOptions} values={values} onChange={(event) => setValues(event.target.values)} />}</Stateful>
            </FormRow>
        </Demo>
    </Section>
)

const DateTimeFields = () => (
    <Section title='Date and time'>
        <Demo name='DatePicker'>
            <FieldStates
                value='2026-10-19'
                render={(props, value, setValue) => <DatePicker {...props} allowEmpty value={value} onChange={(event) => setValue(event.target.value)} />}
            />
        </Demo>

        <Demo name='DatePicker (range)'>
            <FormRow>
                {[
                    {key: 'default', label: 'Range'},
                    {key: 'disabled', label: 'Range disabled', disabled: true},
                    {key: 'readOnly', label: 'Range readOnly', readOnly: true},
                    {key: 'invalid', label: 'Range invalid', isInvalid: 'Choose at most 7 days'}
                ].map((state) => (
                    <Stateful key={state.key} initial={{startDate: '2026-10-19', endDate: '2026-10-23'}}>
                        {(range, setRange) => (
                            <DatePicker
                                label={state.label}
                                disabled={state.disabled}
                                readOnly={state.readOnly}
                                isInvalid={state.isInvalid}
                                value={range.startDate}
                                range={range}
                                maxRange={31}
                                onChange={(newRange) => newRange.startDate && setRange({startDate: newRange.startDate, endDate: newRange.endDate})}
                            />
                        )}
                    </Stateful>
                ))}
            </FormRow>
        </Demo>

        <Demo name='TimePicker'>
            <FieldStates
                value='12:00'
                placeholder='09:00'
                render={(props, value, setValue) => <TimePicker {...props} allowEmpty value={value} onChange={(event) => setValue(event.target.value)} />}
            />
            <FormRow>
                <Stateful initial={90}>{(value, setValue) => <TimePicker label='Duration (minutes)' duration value={value} onChange={(event) => setValue(event.target.value)} />}</Stateful>
                <div style={{flex: 1}} />
            </FormRow>
        </Demo>
    </Section>
)

const AddressFields = () => (
    <Section title='Addresses'>
        <Demo name='AddressSearch' note='Suggestions come from the backend (needs a login), on this page only the field states work.'>
            <FieldStates
                value={sampleAddress}
                emptyValue={NewAddress()}
                render={(props, address, setAddress) => <AddressSearch {...props} address={address} onChange={(newAddress) => setAddress({...address, ...newAddress})} />}
            />
        </Demo>

        <Demo name='PostalCodeAutocomplete' note='The lookup goes over the socket (needs a login). label, info and invalid are not rendered.'>
            <FormRow>
                {[
                    {key: 'default', label: 'Default'},
                    {key: 'disabled', disabled: true},
                    {key: 'readOnly', readOnly: true}
                ].map((state) => (
                    <Stateful key={state.key} initial={sampleAddress}>
                        {(address, setAddress) => <PostalCodeAutocomplete id={state.key} disabled={state.disabled} readOnly={state.readOnly} address={address} onChange={(newAddress) => setAddress({...address, ...newAddress})} />}
                    </Stateful>
                ))}
            </FormRow>
        </Demo>
    </Section>
)

const PickerFields = () => (
    <Section title='Pickers and uploads'>
        <Demo name='IconPicker'>
            <FieldStates
                value='bike'
                render={(props, value, setValue) => <IconPicker {...props} icons={icons} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
        </Demo>

        <Demo name='ColorPicker'>
            <FieldStates
                value={Colors.routeColors[2]}
                noInvalid
                render={(props, value, setValue) => <ColorPicker {...props} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
        </Demo>

        <Demo name='FileUpload'>
            <FieldStates
                value='factuur.pdf'
                noInvalid
                render={(props, fileName, setFileName) => <FileUpload {...props} accept='.pdf' maxSize={5} fileName={fileName} onChange={(name) => setFileName(name)} />}
            />
        </Demo>

        <Demo name='UploadImage'>
            <FieldStates
                value='/images/logoPlaceholder.png'
                noInvalid
                render={(props, image, setImage) => <UploadImage {...props} subText='PNG or JPG, max 1 MB' maxSize={1} image={image} onChange={setImage} />}
            />
        </Demo>

        <Demo name='VerificationCodeInput' note='Focuses its first box on mount, so it is only rendered on request. label and info are not rendered.'>
            <Stateful initial={false}>
                {(shown, setShown) => shown ?
                    (
                        <FormStack>
                            <S2 style={{margin: 0}}>Default</S2>
                            <VerificationCodeInput onChange={() => {}} />
                            <S2 style={{margin: 0}}>Disabled</S2>
                            <VerificationCodeInput disabled onChange={() => {}} />
                            <S2 style={{margin: 0}}>ReadOnly</S2>
                            <VerificationCodeInput readOnly onChange={() => {}} />
                        </FormStack>
                    ) :
                    (
                        <Row>
                            <Button variant='outline' label='Show VerificationCodeInput (default, disabled, readOnly)' onClick={() => setShown(true)} />
                        </Row>
                    )}
            </Stateful>
        </Demo>
    </Section>
)

const ChoiceControls = () => (
    <Section title='Choice controls'>
        <Demo name='Check'>
            <ChoiceStates render={(props, checked, setChecked) => <Check {...props} checked={checked} onChange={(event) => setChecked(event.target.checked)} />} />
            <FormRow>
                <Check label='Indeterminate' indeterminate onChange={() => {}} />
                <Check label='Indeterminate disabled' indeterminate disabled />
            </FormRow>
        </Demo>

        <Demo name='Option' note='The label is the content of the circle (a weekday letter), there is no info. In order: off, on, disabled off, disabled on, readOnly on.'>
            <FormRow>
                {[
                    {key: 'off', label: 'M', checked: false},
                    {key: 'on', label: 'D', checked: true},
                    {key: 'disabledOff', label: 'W', checked: false, disabled: true},
                    {key: 'disabledOn', label: 'D', checked: true, disabled: true},
                    {key: 'readOnly', label: 'V', checked: true, readOnly: true}
                ].map((state) => (
                    <Stateful key={state.key} initial={state.checked}>
                        {(checked, setChecked) => <Option label={state.label} disabled={state.disabled} readOnly={state.readOnly} checked={checked} onChange={(event) => setChecked(event.target.checked)} />}
                    </Stateful>
                ))}
            </FormRow>
        </Demo>

        <Demo name='RadioButton'>
            <ChoiceStates render={(props, checked, setChecked) => <RadioButton {...props} checked={checked} onChange={() => setChecked(!checked)} />} />
        </Demo>

        <Demo name='RadioGroup'>
            <FieldStates
                value='week'
                noInvalid
                render={(props, value, setValue) => <RadioGroup {...props} options={radioOptions} value={value} onChange={(event) => setValue(event.target.value)} />}
            />
            <FormRow>
                <Stateful initial='bike'>
                    {(value, setValue) => (
                        <RadioGroup
                            label='With icons'
                            options={[{value: 'bike', icon: 'mdi mdi-bike', tooltip: 'Fiets'}, {value: 'van', icon: 'mdi mdi-van-utility', tooltip: 'Bus'}, {value: 'walk', icon: 'mdi mdi-walk', label: 'Lopen'}]}
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                        />
                    )}
                </Stateful>
                <div style={{flex: 1}} />
            </FormRow>
        </Demo>

        <Demo name='Toggle' note='Label left, switch right over the full width, so shown as a list.'>
            <ChoiceStates vertical render={(props, checked, setChecked) => <Toggle {...props} checked={checked} onChange={(event) => setChecked(event.target.checked)} />} />
        </Demo>

        <Demo name='Slider'>
            <FormRow>
                {[
                    {key: 'default', label: 'Default'},
                    {key: 'disabled', label: 'Disabled', disabled: true},
                    {key: 'readOnly', label: 'ReadOnly', readOnly: true},
                    {key: 'info', label: 'With info', info: 'Explanation shown in the info tooltip'}
                ].map((state) => (
                    <Stateful key={state.key} initial={[32, 68]}>
                        {(values, setValues) => (
                            <Slider
                                label={state.label}
                                disabled={state.disabled}
                                readOnly={state.readOnly}
                                info={state.info}
                                min={0}
                                max={96}
                                step={1}
                                diff={4}
                                value1={values[0]}
                                value2={values[1]}
                                value1Icon='mdi-arrow-expand-left'
                                value2Icon='mdi-arrow-expand-right'
                                valuesToLabel={valuesToLabel}
                                onChange={(value1, value2) => setValues([value1, value2])}
                            />
                        )}
                    </Stateful>
                ))}
            </FormRow>
        </Demo>
    </Section>
)

const Layout = () => (
    <Section title='Layout'>
        <Demo name='FormStack with FormRows' note='Short controls (Button, IconButton, Toggle, Check) centre themselves on the first line of the fields.'>
            <Stateful initial={{name: 'Jan', km: '12', active: true, check: false, notes: ''}}>
                {(form, setForm) => {
                    const set = (key) => (value) => setForm({...form, [key]: value})

                    return (
                        <FormStack>
                            <FormRow>
                                <Input label='Name' value={form.name} onChange={(event) => set('name')(event.target.value)} />
                                <Button variant='outline' icon='mdi mdi-content-save' label='Button' />
                            </FormRow>
                            <FormRow gap={formSpacing.tight}>
                                <Input label='Distance (gap formSpacing.tight)' append='km' value={form.km} onChange={(event) => set('km')(event.target.value)} />
                                <IconButton icon='mdi mdi-delete' />
                            </FormRow>
                            <FormRow>
                                <Input label='Field' value={form.name} onChange={(event) => set('name')(event.target.value)} />
                                <Toggle label='Toggle' checked={form.active} onChange={(event) => set('active')(event.target.checked)} />
                                <Check label='Check' checked={form.check} onChange={(event) => set('check')(event.target.checked)} />
                            </FormRow>
                            <FormRow>
                                <Input label='Invalid with message' isInvalid='Message below the field' value={form.name} onChange={(event) => set('name')(event.target.value)} />
                                <Button variant='outline' label='Button' />
                                <IconButton icon='mdi mdi-pencil' />
                            </FormRow>
                            <FormRow>
                                <TextArea label='TextArea next to an Input' value={form.notes} onChange={(event) => set('notes')(event.target.value)} />
                                <Input label='Input' value={form.name} onChange={(event) => set('name')(event.target.value)} />
                            </FormRow>
                        </FormStack>
                    )
                }}
            </Stateful>
        </Demo>

        <Demo name='Card with a form' note='Padding formSpacing.inset, so the fields sit as far from the edge as from each other.'>
            <Stateful initial={{street: 'Damrak', nr: '1', city: 'Amsterdam', type: 'cargo', business: false}}>
                {(form, setForm) => {
                    const set = (key) => (value) => setForm({...form, [key]: value})

                    return (
                        <Card style={{background: Colors.backgroundNeutral, padding: formSpacing.inset, maxWidth: 600}}>
                            <FormStack>
                                <FormRow>
                                    <Input label='Street' value={form.street} onChange={(event) => set('street')(event.target.value)} />
                                    <Input label='Nr' style={{width: 80, flex: 'none'}} value={form.nr} onChange={(event) => set('nr')(event.target.value)} />
                                </FormRow>
                                <Input label='City' value={form.city} onChange={(event) => set('city')(event.target.value)} />
                                <FormRow>
                                    <Select label='Vehicle' options={options} value={form.type} onChange={(event) => set('type')(event.target.value)} />
                                    <Toggle label='Business' checked={form.business} onChange={(event) => set('business')(event.target.checked)} />
                                </FormRow>
                                <Input label='Disabled' disabled value='Shown on the card background' />
                            </FormStack>
                        </Card>
                    )
                }}
            </Stateful>
        </Demo>

        <Demo name='Button row' note='Buttons next to each other with formSpacing.columnGap.'>
            <Row style={{columnGap: formSpacing.columnGap}}>
                <Button variant='outline' label='Cancel' />
                <Button variant='text' label='Text' />
                <Button icon='mdi mdi-check' label='Save' />
            </Row>
        </Demo>
    </Section>
)

const OtherComponents = () => (
    <Section title='Other components'>
        <Demo name='Alert'>
            <Alert style={{marginBottom: 6}}>Alert</Alert>
            <Alert style={{marginBottom: 6}} variant='info'>Alert info</Alert>
            <Alert style={{marginBottom: 6}} variant='warning'>Alert warning</Alert>
            <Alert style={{marginBottom: 6}} variant='error'>Alert error</Alert>
        </Demo>

        <Demo name='Badge'>
            <Row style={{columnGap: formSpacing.columnGap}}>
                <Badge>Badge</Badge>
                <Badge variant='info'>Badge</Badge>
                <Badge variant='warning'>Badge</Badge>
                <Badge variant='error'>Badge</Badge>
            </Row>
        </Demo>

        <Demo name='Button and IconButton'>
            {[undefined, 'text', 'outline-white'].map((variant) => (
                <Row key={variant || 'default'} style={{columnGap: formSpacing.columnGap}}>
                    <Button variant={variant} icon='mdi mdi-alien' label='Button' />
                    <Button variant={variant} selected icon='mdi mdi-alien' label='Selected' />
                    <Button variant={variant} disabled icon='mdi mdi-alien' label='Disabled' />
                </Row>
            ))}
            <Row style={{columnGap: formSpacing.columnGap}}>
                <IconButton icon='mdi mdi-check' />
                <IconButton icon='mdi mdi-check' disabled />
            </Row>
        </Demo>

        <Demo name='Tabs'>
            <Tabs style={{marginBottom: 30}} value='1'>
                <Tab value='1'>Tab 1</Tab>
                <Tab value='2'>Tab 2</Tab>
                <Tab value='3' disabled>Tab 3</Tab>
            </Tabs>
        </Demo>
    </Section>
)

const Components = () => {
    return (
        <div style={{width: '100%', height: '100vh', overflow: 'auto', background: Colors.backgroundWhite}}>
            <div style={{display: 'flex', flexDirection: 'column', gap: formSpacing.section * 2, maxWidth: 1200, margin: '0 auto', padding: '48px 24px'}}>
                <FormStack>
                    <H3 style={{margin: 0}}>Form components</H3>
                    <P>
                        Every form component in every state. Sizes, spacing and colours of the fields come from the tokens in
                        {' '}<code>src/components/UI/forms/styles.ts</code> (<code>field</code>, <code>label</code>, <code>formSpacing</code>, <code>formColors</code>),
                        the look of Check, Option, RadioButton, RadioGroup and Toggle from <code>choiceStyles.ts</code> and the field box,
                        floating label, prepend/append and invalid icon from <code>FieldBox.tsx</code>. Change a token there and check the result here.
                    </P>
                    <P>
                        Layout: <code>FormStack</code> stacks items vertically with <code>formSpacing.rowGap</code> ({formSpacing.rowGap}px),
                        {' '}<code>FormRow</code> places them next to each other with <code>formSpacing.columnGap</code> ({formSpacing.columnGap}px).
                        Fields have no margins of their own, all space in a form comes from these two. A card that holds fields gets padding
                        {' '}<code>formSpacing.inset</code> ({formSpacing.inset}px).
                    </P>
                </FormStack>

                <TextFields />
                <NumberFields />
                <SelectFields />
                <DateTimeFields />
                <AddressFields />
                <PickerFields />
                <ChoiceControls />
                <Layout />
                <OtherComponents />
            </div>
        </div>
    )
}

export default Components
